import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { getApiUrl } from '../config';
import { trackJoinChallengeClicked, trackJoinCodeValidationResult, } from '../tracking';
import '../styles/JoinClassModal.css';
// API base URL from config
const API_BASE_URL = getApiUrl();
/**
 * Validates a join code against the backend.
 * @param joinCode - The join code to validate
 * @returns Promise<ValidateJoinCodeResponse> - Validation result with event details
 */
const validateJoinCode = async (joinCode) => {
    try {
        const response = await fetch(`${API_BASE_URL}/v1/events/validate/${joinCode}`);
        if (!response.ok) {
            // Handle non-200 responses
            if (response.status === 404) {
                return { isValid: false };
            }
            throw new Error(`HTTP error: ${response.status}`);
        }
        const data = await response.json();
        return {
            isValid: data.isValid === true,
            eventName: data.eventName,
            sessionId: data.sessionId,
        };
    }
    catch (error) {
        console.error('Error validating join code:', error);
        throw error; // Re-throw to be handled by the caller
    }
};
const JoinClassModal = ({ isOpen, onClose, onJoin }) => {
    const [joinCode, setJoinCode] = useState('');
    const [isValid, setIsValid] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [error, setError] = useState(null);
    // Inline charset feedback as the user types. The input is already auto-uppercased
    // on change, so we only need to flag characters outside [A-Z0-9]. Length is
    // enforced separately by maxLength={9} on the input element.
    const charsetError = joinCode.length > 0 && !/^[A-Z0-9]*$/.test(joinCode)
        ? 'Join code must only contain letters A–Z and digits 0–9.'
        : null;
    useEffect(() => {
        // Validate join code format (exactly 9 alphanumeric characters)
        setIsValid(/^[A-Z0-9]{9}$/.test(joinCode));
        setError(null);
    }, [joinCode]);
    // Handle escape key to close modal
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = '';
        };
    }, [isOpen, onClose]);
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isValid || isValidating)
            return;
        trackJoinChallengeClicked('Join Challenge', joinCode || undefined);
        setIsValidating(true);
        setError(null);
        try {
            const result = await validateJoinCode(joinCode);
            if (result.isValid) {
                trackJoinCodeValidationResult('success', joinCode);
                // Store the valid join code in sessionStorage
                sessionStorage.setItem('HOF_2026_JC', joinCode);
                if (!result.sessionId) {
                    throw new Error('Missing sessionId in validation response');
                }
                onJoin(joinCode, result.sessionId);
            }
            else {
                trackJoinCodeValidationResult('Failed', joinCode);
                setError('Invalid join code. Please check the code and try again.');
            }
        }
        catch (err) {
            console.error('Validation error:', err);
            trackJoinCodeValidationResult('Failed', joinCode);
            setError('Unable to validate code. Please check your connection and try again.');
        }
        finally {
            setIsValidating(false);
        }
    };
    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };
    const handleBackClick = () => {
        trackJoinChallengeClicked('Back', joinCode || undefined);
        onClose();
    };
    if (!isOpen)
        return null;
    return (_jsx("div", { className: "modal-overlay", onClick: handleBackdropClick, children: _jsxs("div", { className: "modal-container", role: "dialog", "aria-modal": "true", "aria-labelledby": "modal-title", children: [_jsx("div", { className: "modal-header", children: _jsx("button", { className: "modal-close-btn", onClick: onClose, "aria-label": "Close modal", children: _jsx("svg", { width: "20", height: "20", viewBox: "0 0 20 20", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: "M15 5L5 15M5 5L15 15", stroke: "#5D686F", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }) }) }) }), _jsxs("div", { className: "modal-content", children: [_jsxs("div", { className: "modal-text-block", children: [_jsx("h2", { id: "modal-title", className: "modal-heading", children: "Join your class" }), _jsx("p", { className: "modal-body", children: "Enter the join code provided by your teacher to access the Hour of Finance Challenge." })] }), _jsx("form", { onSubmit: handleSubmit, className: "modal-form", children: _jsxs("div", { className: "form-field", children: [_jsx("label", { htmlFor: "join-code", className: "field-label", children: "Join Code" }), _jsx("div", { className: "input-wrapper", children: _jsx("input", { id: "join-code", type: "text", className: `text-input ${error || charsetError ? 'text-input-error' : ''}`, placeholder: "Enter code", value: joinCode, onChange: (e) => setJoinCode(e.target.value.toUpperCase()), autoComplete: "off", autoFocus: true, disabled: isValidating, maxLength: 9 }) }), (charsetError || error) && (_jsx("p", { className: "field-error", children: charsetError || error }))] }) })] }), _jsx("div", { className: "modal-footer", children: _jsxs("div", { className: "footer-actions", children: [_jsx("button", { type: "button", className: "btn-secondary", onClick: handleBackClick, disabled: isValidating, children: "Back" }), _jsx("button", { type: "submit", className: `btn-primary ${!isValid || isValidating ? 'btn-disabled' : ''}`, disabled: !isValid || isValidating, onClick: handleSubmit, children: isValidating ? 'Validating...' : 'Join Challenge' })] }) })] }) }));
};
export default JoinClassModal;
